import { useQuery } from '@tanstack/react-query'
import { ActionBar, Modal, Text } from '@viapeople/components'

import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { ModalErrorState, ModalLoadingState } from '../../components/ModalStateHandlers'
import { sessionStore } from '../../sharedStores/sessionStore'
import { formatStepName } from '../../utils/helpers'
import { trpc } from '../../utils/trpc'
import { AiSummaryList } from '../cycles/cycle-execution/AiSummaryList'
import { ComponentRenderer } from '../cycles/cycle-execution/ComponentRenderer'
import { EmployeeInfo, Header, Sidebar } from '../cycles/cycle-execution/components'
import { PdfDownloadButton } from '../cycles/cycle-execution/PdfDownloadButton'
import type {
	AccessLevel,
	CycleStepActions,
	FormComponent,
	JumpToItem,
	ProcessedFormComponent,
} from '../cycles/cycle-execution/types'

type FormValues = Record<string, string | number | boolean>

export const useCycleExecutionViewer = ({ evaluationId }: { evaluationId: string }) => {
	const {
		data: formData,
		refetch,
		isLoading,
		isError,
		error,
	} = useQuery({
		...trpc.client.viewers.getEvaluationForm.queryOptions({
			evaluationId: evaluationId,
		}),
		enabled: !!evaluationId,
	})

	const cycleStepFormatted = formData?.evaluation?.cycleStep.map(formatStepName).join(', ') || ''
	const cycleStepData = {
		id: formData?.evaluation?.cycleStep.join(', ') || '',
		name: cycleStepFormatted || '-',
	}

	const formComponents = formData?.formComponents || []

	const stepAccessLevel: AccessLevel = 'read'

	const collectJumpToItems = (components: FormComponent[], items: JumpToItem[] = []): JumpToItem[] => {
		components.forEach((component) => {
			if (component.name === 'container' || component.name === 'repeatable') {
				const customData = component.custom as Record<string, unknown>
				const label = typeof customData?.label === 'string' ? customData.label : ''

				if (label) {
					items.push({ id: component.id, label })
				}

				const children = component.children as FormComponent[] | undefined
				const shouldJumpToChildren = children?.length && component.name !== 'repeatable'
				if (shouldJumpToChildren) {
					collectJumpToItems(children, items)
				}
			}
		})

		return items
	}

	const jumpToItems = collectJumpToItems(formComponents as FormComponent[])

	const cycleStepActions: CycleStepActions = {
		headerActions: [],
		signatureActions: [],
		bottomActions: [],
	}

	const canEdit = false

	return {
		cycle: { name: formData?.evaluation?.cycleName },
		cycleStep: cycleStepData,
		formComponents,
		aiSummaries: [],
		refetch,
		calculatedRole: 'viewer',
		stepAccessLevel,
		formData: formData || null,
		taskData: formData || null,
		jumpToItems,
		cycleStepActions,
		requiresSignature: false,
		allowDenial: false,
		canEdit,
		isLoading,
		isError,
		technicalError: error?.message,
	}
}

export const CycleExecutionViewer = () => {
	const navigate = useNavigate()
	const { evaluationId } = useParams<{ evaluationId: string }>()

	const methods = useForm<FormValues>()
	const { control } = methods

	const {
		cycle,
		cycleStep,
		formComponents,
		aiSummaries,
		jumpToItems,
		taskData,
		refetch,
		isLoading,
		isError,
		technicalError,
	} = useCycleExecutionViewer({
		evaluationId: evaluationId || '',
	})

	const evaluationIdValue = taskData?.evaluation?.id || ''
	const currentStepId = cycleStep?.id || ''
	const userRole = 'viewer'
	const canEdit = false

	const handleClose = () => {
		navigate('/dashboard')
	}

	const bottomActions = [
		{
			label: 'Close',
			onClick: handleClose,
			variant: 'secondary' as const,
			disabled: false,
		},
	]

	if (isLoading) {
		return <ModalLoadingState onClose={() => navigate(-1)} />
	}

	if (isError) {
		return (
			<ModalErrorState
				errorMessage='Unable to load evaluation'
				errorDescription='There was a problem loading the evaluation. Please try again or go back.'
				technicalDetails={technicalError}
				logoUrl={sessionStore.getState().organization?.logo ?? undefined}
				onClose={() => navigate(-1)}
				onRetry={() => refetch()}
			/>
		)
	}

	return (
		<Modal
			isOpen={true}
			onClose={() => navigate(-1)}
			fullContent={true}
			fullScreen={true}
			title={`${cycleStep?.name || ''} (View Only)`}
			bodyClassName='h-full'>
			<FormProvider {...methods}>
				<div className='flex flex-col h-full'>
					<div className='flex flex-1 overflow-hidden'>
						<div className='sidebar no-print flex flex-col w-70 border-r border-neutral-200 overflow-y-auto pb-44'>
							<Sidebar jumpToItems={jumpToItems} evaluateeData={taskData?.participant ?? {}} />
							<div className='flex flex-col px-4 py-4 border-b border-neutral-200'>
								<Text variant='xsmall' className='text-neutral-400 mb-3'>
									Actions
								</Text>
								<PdfDownloadButton variant='secondary' size='small' className='w-full' />
							</div>
						</div>

						<div className='cycle-execution-content flex flex-col flex-1 gap-4 p-6 overflow-y-auto pb-44'>
							<Header
								breadcrumb={[
									{ label: 'Dashboard', to: '/dashboard' },
									{
										label: cycle?.name || '',
									},
									{
										label: cycleStep?.name || '',
									},
								]}
								headerActions={[]}
								onActionClick={() => {}}
							/>

							<EmployeeInfo evaluateeData={taskData?.participant ?? {}} variant='print' className='print-only' />

							{formComponents.map((component) => (
								<div key={component.id}>
									<ComponentRenderer
										key={component.id}
										component={component as unknown as ProcessedFormComponent}
										control={control}
										evaluationId={evaluationIdValue}
										currentStepId={currentStepId}
										userRole={userRole}
										canEdit={canEdit}
									/>
								</div>
							))}

							{aiSummaries && aiSummaries.length > 0 && (
								<div className='mt-6'>
									<AiSummaryList aiSummaries={aiSummaries} evaluationId={evaluationIdValue} onRefresh={refetch} />
								</div>
							)}
						</div>
					</div>

					<ActionBar variant='modal' className='left-0' rightActions={bottomActions} />
				</div>
			</FormProvider>
		</Modal>
	)
}
